import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { ArrowLeft, Calendar as CalendarIcon, Users, Phone, Mail } from 'lucide-react';
import { format } from 'date-fns';
import { enUS, fr, es, de, it, nl, ru, ar, zhCN, ja, ko } from 'date-fns/locale';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSelector } from '../components/LanguageSelector';
import { getPropertyName } from '../translations/propertyTranslations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const localeMap = {
  fr: fr, en: enUS, es: es, de: de, it: it, nl: nl, ru: ru, ar: ar, zh: zhCN, ja: ja, ko: ko, hi: enUS
};

function BookingPage() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const dateLocale = localeMap[language] || enUS;

  const [formData, setFormData] = useState({
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    check_in: null,
    check_out: null,
    guests: 1,
    message: ''
  });

  useEffect(() => {
    fetchProperty();
  }, [propertyId]);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`${API}/properties/${propertyId}`);
      setProperty(response.data);
    } catch (error) {
      console.error('Error fetching property:', error);
      toast.error(t('booking.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.guest_name || !formData.guest_email || !formData.guest_phone) {
      toast.error(t('booking.errorMessage'));
      return;
    }

    if (!formData.check_in || !formData.check_out) {
      toast.error(t('booking.errorMessage'));
      return;
    }

    setSubmitting(true);

    try {
      const requestData = {
        property_id: property.id,
        property_name: property.name,
        guest_name: formData.guest_name,
        guest_email: formData.guest_email,
        guest_phone: formData.guest_phone,
        check_in: format(formData.check_in, 'yyyy-MM-dd'),
        check_out: format(formData.check_out, 'yyyy-MM-dd'),
        guests: parseInt(formData.guests),
        message: formData.message
      };

      await axios.post(`${API}/booking-requests`, requestData);
      
      toast.success(t('booking.successMessage'));
      
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error('Error submitting booking:', error);
      toast.error(t('booking.errorMessage'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-4">Property not found</h2>
          <Button onClick={() => navigate('/')}>{t('booking.backToHome')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header with language selector */}
      <div className="bg-white border-b border-border py-2 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-end gap-4 text-sm">
          <LanguageSelector />
          <div className="flex items-center gap-2">
            <Phone size={16} className="text-accent" />
            <a href="tel:+33611518866" className="hover:text-accent">+33 6 11 51 88 66</a>
          </div>
          <div className="flex items-center gap-2">
            <Mail size={16} className="text-accent" />
            <a href="mailto:guiauchaindiane@orange.fr" className="hover:text-accent">guiauchaindiane@orange.fr</a>
          </div>
        </div>
      </div>

      <div className="py-12 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <Button
            data-testid="back-btn"
            variant="ghost"
            onClick={() => navigate(`/property/${propertyId}`)}
            className="mb-6"
          >
            <ArrowLeft size={20} className="mr-2" />
            {t('propertyDetail.backToProperties')}
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-3xl">{t('booking.title')}</CardTitle>
                  <p className="text-muted-foreground mt-2">
                    {t('booking.successMessage').replace('!', '.')}
                  </p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="guest_name">{t('booking.fullName')} *</Label>
                      <Input
                        data-testid="guest-name-input"
                        id="guest_name"
                        name="guest_name"
                        value={formData.guest_name}
                        onChange={handleInputChange}
                        placeholder="John Doe"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="guest_email">{t('booking.email')} *</Label>
                      <Input
                        data-testid="guest-email-input"
                        id="guest_email"
                        name="guest_email"
                        type="email"
                        value={formData.guest_email}
                        onChange={handleInputChange}
                        placeholder="john.doe@example.com"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="guest_phone">{t('booking.phone')} *</Label>
                      <Input
                        data-testid="guest-phone-input"
                        id="guest_phone"
                        name="guest_phone"
                        type="tel"
                        value={formData.guest_phone}
                        onChange={handleInputChange}
                        placeholder="+33 6 12 34 56 78"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t('booking.checkIn')} *</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              data-testid="check-in-date-btn"
                              variant="outline"
                              className="w-full justify-start text-left font-normal"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {formData.check_in ? format(formData.check_in, 'PPP', { locale: dateLocale }) : t('booking.checkIn')}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={formData.check_in}
                              onSelect={(date) => setFormData(prev => ({ ...prev, check_in: date }))}
                              disabled={(date) => date < new Date()}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      <div className="space-y-2">
                        <Label>{t('booking.checkOut')} *</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              data-testid="check-out-date-btn"
                              variant="outline"
                              className="w-full justify-start text-left font-normal"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {formData.check_out ? format(formData.check_out, 'PPP', { locale: dateLocale }) : t('booking.checkOut')}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={formData.check_out}
                              onSelect={(date) => setFormData(prev => ({ ...prev, check_out: date }))}
                              disabled={(date) => date < (formData.check_in || new Date())}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="guests">{t('booking.guests')} *</Label>
                      <div className="flex items-center gap-2">
                        <Users size={20} className="text-muted-foreground" />
                        <Input
                          data-testid="guests-input"
                          id="guests"
                          name="guests"
                          type="number"
                          min="1"
                          max={property.max_guests}
                          value={formData.guests}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <p className="text-sm text-muted-foreground">Maximum: {property.max_guests} {t('properties.guests')}</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">{t('booking.message')}</Label>
                      <Textarea
                        data-testid="message-textarea"
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder={t('booking.messagePlaceholder')}
                        rows={4}
                      />
                    </div>

                    <Button
                      data-testid="submit-booking-btn"
                      type="submit"
                      className="w-full bg-accent hover:bg-accent/90 h-12 rounded-full font-medium"
                      disabled={submitting}
                    >
                      {submitting ? t('booking.submitting') : t('booking.submit')}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle>{t('booking.property')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <img
                      src={property.images[0]}
                      alt={getPropertyName(property.id, language) || property.name}
                      className="w-full h-32 object-cover rounded-lg mb-3"
                    />
                    <h4 className="font-semibold text-lg">{getPropertyName(property.id, language) || property.name}</h4>
                    <p className="text-sm text-muted-foreground">{property.type === 'villa' ? t('properties.villa') : 'Apartment'}</p>
                  </div>

                  <div className="pt-4 border-t">
                    {formData.check_in && formData.check_out && (
                      <>
                        <div className="flex justify-between mb-2">
                          <span className="text-muted-foreground">Duration</span>
                          <span className="font-semibold">
                            {Math.ceil((formData.check_out - formData.check_in) / (1000 * 60 * 60 * 24))} {t('propertyDetail.perNight').replace('/', '')}s
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BookingPage;
